import { RECENT_LIST_OVERLAY_MS } from '@/lib/companyApplicationsList';
import { shouldClearProcessingIndicator } from '@/lib/bulkProcessQueueSync';
import type { Application } from '@/types';

/** Session overlay TTL — workday reloads; separate from 12m hot refetch merge window. */
export const RECENT_SETTLED_STORAGE_MS = 24 * 60 * 60 * 1000;

const STORAGE_PREFIX = 'hiromatch_recent_settled_apps:';

type StoredPayload = {
  savedAt: number;
  apps: Application[];
};

function storageKey(companyId: string): string {
  return `${STORAGE_PREFIX}${companyId}`;
}

function readPayload(companyId: string): StoredPayload | null {
  if (typeof sessionStorage === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(storageKey(companyId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredPayload;
    if (!parsed?.apps?.length || typeof parsed.savedAt !== 'number') return null;
    if (Date.now() - parsed.savedAt > RECENT_SETTLED_STORAGE_MS) {
      sessionStorage.removeItem(storageKey(companyId));
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function writePayload(companyId: string, apps: Application[]): void {
  if (typeof sessionStorage === 'undefined' || apps.length === 0) return;
  try {
    const payload: StoredPayload = { savedAt: Date.now(), apps };
    sessionStorage.setItem(storageKey(companyId), JSON.stringify(payload));
  } catch {
    // Quota exceeded — best-effort overlay only.
  }
}

/** Rows that finished screening and should survive F5 before list API catches up. */
export function isRecentSettledListRow(app: Pick<Application, 'state'>): boolean {
  return shouldClearProcessingIndicator(app.state)
    && app.state !== 'READY_TO_PROCESS'
    && app.state !== 'PENDING_UPGRADE'
    && app.state !== 'APPLIED_NEW';
}

/**
 * Merge newly settled rows into session overlay (survives bulk queue clear + reload).
 */
export function persistRecentSettledApplications(
  companyId: string,
  updates: Application[],
): void {
  const settled = updates.filter(isRecentSettledListRow);
  if (settled.length === 0) return;

  const existing = readPayload(companyId);
  const byId = new Map<string, Application>();
  for (const app of existing?.apps ?? []) {
    byId.set(app.id, app);
  }
  for (const app of settled) {
    const prev = byId.get(app.id);
    if (!prev) {
      byId.set(app.id, app);
      continue;
    }
    byId.set(app.id, {
      ...prev,
      ...app,
      candidateFirstName: app.candidateFirstName ?? prev.candidateFirstName,
      candidateLastName: app.candidateLastName ?? prev.candidateLastName,
      candidateEmail: app.candidateEmail ?? prev.candidateEmail,
      updatedAt: app.updatedAt ?? prev.updatedAt,
    });
  }
  writePayload(companyId, Array.from(byId.values()));
}

export function loadRecentSettledApplications(companyId: string): Application[] {
  return readPayload(companyId)?.apps ?? [];
}

/** Ids that must stay in list merge even when updatedAt is older than RECENT_LIST_OVERLAY_MS. */
export function getRecentSettledRetainIds(companyId: string): Set<string> {
  return new Set(loadRecentSettledApplications(companyId).map((a) => a.id));
}

export function clearRecentSettledApplications(companyId: string): void {
  if (typeof sessionStorage === 'undefined') return;
  sessionStorage.removeItem(storageKey(companyId));
}

/** Drop permanently deleted applications from the session overlay. */
export function removeRecentSettledApplications(
  companyId: string,
  ids: Iterable<string>,
): void {
  const idSet = ids instanceof Set ? ids : new Set(ids);
  if (idSet.size === 0) return;
  const existing = readPayload(companyId);
  if (!existing?.apps.length) return;
  const filtered = existing.apps.filter((app) => !idSet.has(app.id));
  if (filtered.length === existing.apps.length) return;
  if (filtered.length === 0) {
    clearRecentSettledApplications(companyId);
    return;
  }
  writePayload(companyId, filtered);
}
